import { useAppSelector } from "integrations/redux/hooks"
import { useCallback } from "react"
import { createTransferableFromOptions, useTransferSettings } from "integrations/transfer"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import {
    selectActiveWorkspace,
    selectLegacyWorkspaceTree,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFire } from "features/map/control/selectors"
import { findTagAncestors } from "features/data-management/workspace/module"
import { selectAllEntitiesMap, selectPdfEntitiesMap } from "panels/content/side-panel/slice"
import { getAncestors } from "panels/content/side-panel/utils"

export const useDataTransfer = () => {
    const transferIntegrationSettings = useTransferSettings()
    const workspace = useAppSelector(selectActiveWorkspace())
    const project = useAppSelector(selectActivatedFire)
    const tagTree = useAppSelector(selectLegacyWorkspaceTree)
    const entities = useAppSelector(selectAllEntitiesMap)
    const pdfEntities = useAppSelector(selectPdfEntitiesMap)
    const canTransfer =
        !!transferIntegrationSettings?.enabled && !!workspace && !!project && !!tagTree
    const transfer = useCallback(
        async (
            kind: Parameters<typeof createTransferableFromOptions>[0],
            ids: string[],
            groupId = ids[0] ?? ""
        ) => {
            if (!canTransfer) return
            await fetchApiGateway("post /org/:orgId/project/:projectId/data-transfer", {
                params: { orgId: workspace.id, projectId: project.id },
                body: {
                    tags: findTagAncestors(tagTree, project.tagId).map((e) => e.name),
                    groups: getAncestors(groupId, kind === "pdf" ? pdfEntities : entities)
                        .reverse()
                        .map((e) => e.name),
                    transfer: ids.map((id) =>
                        createTransferableFromOptions(kind, id, transferIntegrationSettings)
                    ),
                },
            })
        },
        [
            canTransfer,
            workspace,
            project?.id,
            project?.tagId,
            tagTree,
            entities,
            pdfEntities,
            transferIntegrationSettings,
        ]
    )
    return {
        canTransfer,
        transfer,
        integrationName: canTransfer ? transferIntegrationSettings.integrationName : undefined,
    }
}
